import { Fuel, VehicleModelWithInsurancesForD2CDto } from '@orcar/common';
import {
  filterOptionAtom,
  getVehicleModelDisplayPrice,
  SearchParamsType,
  useRecoil,
} from '@orcar/common-d2c';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import ReservationStepper from '@/components/reservation/ReservationStepper';
import ReserveLoadingOverlay from '@/components/reservation/ReserveLoadingOverlay';
import { useReserveAndPay } from '@/hooks/useReserveAndPay';
import { trackCarDetailedViewed } from '@/utils/analytics';
import { mobonTrackProductView } from '@/utils/mobon';
import { getDefaultInsuranceName } from '@/utils/vehicle-model';
import SearchFilterNull from './vehicleCard/SearchFilterNull';
import SearchResultCard from './vehicleCard/SearchResultCard';

const SORT_OPTIONS = [
  { id: 'price', name: '낮은 가격순' },
  { id: 'price_desc', name: '높은 가격순' },
];

export type SearchResultProps = {
  searchOptions: SearchParamsType;
  vehicleModelList?: VehicleModelWithInsurancesForD2CDto[];
  isWithPet?: boolean;
};

const SearchResult = ({
  searchOptions,
  vehicleModelList,
  isWithPet,
}: SearchResultProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { reserveAndPay, isReserving } = useReserveAndPay();

  const [sortBy, setSortBy] = useState('price'); // 기본값: 낮은 가격순
  const {
    recoilState: [recoilFilterOption, setRecoilFilterOption],
  } = useRecoil(filterOptionAtom);

  // 정렬된 차량 목록
  const sortedVehicleList = vehicleModelList
    ? [...vehicleModelList].sort((a, b) => {
        // 모든 정렬에서 예약 가능한 차량을 우선으로 정렬
        if (a.availableCount === 0 && b.availableCount > 0) return 1;
        if (a.availableCount > 0 && b.availableCount === 0) return -1;

        const priceA = getVehicleModelDisplayPrice(a) ?? 0;
        const priceB = getVehicleModelDisplayPrice(b) ?? 0;
        // 높은 가격순 / 낮은 가격순
        return sortBy === 'price_desc' ? priceB - priceA : priceA - priceB;
      })
    : vehicleModelList;

  useEffect(() => {
    const carType = searchParams.get('carType');
    const carFuel = searchParams.get('carFuel');
    const sort = searchParams.get('sort');
    const isWithPet = searchParams.get('isWithPet') === 'true';

    if (sort) {
      setSortBy(sort);
    }

    // 펫 모드("펫과 함께")에서는 차종/연료를 recoil에 복원하지 않는다.
    // (펫은 별도 뷰이며 차종/연료 필터와 상호배타적)
    if (!isWithPet && (carType || carFuel)) {
      const newFilterOption = { ...recoilFilterOption };

      if (carType) {
        newFilterOption.carTypeList = [carType];
      }

      if (carFuel) {
        newFilterOption.carFuelList = [carFuel];
      }

      setRecoilFilterOption(newFilterOption);
    }
  }, [searchParams, setRecoilFilterOption]);

  useEffect(() => {
    const newSearchParams = new URLSearchParams(searchParams);
    const isWithPet = newSearchParams.get('isWithPet') === 'true';

    // 펫 모드에서는 차종/연료를 URL에 쓰지 않는다.
    // (펫 = 별도 뷰이므로 isWithPet과 carType/carFuel이 섞이지 않게 한다)
    if (isWithPet) {
      newSearchParams.delete('carType');
      newSearchParams.delete('carFuel');
    } else {
      if (recoilFilterOption.carTypeList.length === 1) {
        newSearchParams.set('carType', recoilFilterOption.carTypeList[0]);
      } else {
        newSearchParams.delete('carType');
      }

      if (
        recoilFilterOption.carFuelList.length === 1 &&
        recoilFilterOption.carFuelList.includes(Fuel.EV)
      ) {
        newSearchParams.set('carFuel', Fuel.EV);
      } else {
        newSearchParams.delete('carFuel');
      }
    }

    // 정렬 파라미터 추가 (기본 낮은 가격순은 URL에 남기지 않음)
    if (sortBy !== 'price') {
      newSearchParams.set('sort', sortBy);
    } else {
      newSearchParams.delete('sort');
    }

    setSearchParams(newSearchParams, { replace: true });
  }, [
    recoilFilterOption.carTypeList,
    recoilFilterOption.carFuelList,
    sortBy,
    searchParams,
    setSearchParams,
  ]);

  const handleReserve = (
    vehicleModel: VehicleModelWithInsurancesForD2CDto,
    insuranceName?: string,
  ) => {
    if (isReserving) return;

    // 차량 선택 이벤트 트래킹 (예약 진행 시작)
    trackCarDetailedViewed({
      car_id: String(vehicleModel.id),
      car_model: vehicleModel.name,
      car_type: vehicleModel.type,
      is_with_pet: isWithPet,
      source: 'search_result',
    });

    // 모비온 상품(차량) 수집 — 다이나믹 리타겟팅
    const finalPrice = vehicleModel.insurances?.[0]?.finalPrice;
    const hasAnchor = vehicleModel.referencePrice > 0;
    mobonTrackProductView({
      productCode: String(vehicleModel.id),
      productName: vehicleModel.name,
      price: hasAnchor ? vehicleModel.referencePrice : finalPrice,
      dcPrice: hasAnchor ? finalPrice : undefined,
      imageUrl: vehicleModel.imageUrls?.[0],
      topCategory: vehicleModel.type,
    });

    // 검색 카드의 보험 카드를 선택하면 그 보험으로, 아니면 기본 보험으로
    // 사전예약 후 상세/결제 단계에서 보험을 변경할 수 있다.
    reserveAndPay({
      vehicleModelId: vehicleModel.id,
      carType: vehicleModel.type,
      insuranceName: insuranceName ?? getDefaultInsuranceName(vehicleModel),
      pickUpAt: searchOptions.pickUpAt,
      dropOffAt: searchOptions.dropOffAt,
      isWithPet: !!isWithPet,
      source: 'search_result',
    });
  };

  const handleSortChange = (sortId: string) => {
    setSortBy(sortId);
  };

  return (
    <>
      {isReserving && <ReserveLoadingOverlay />}
      <div className='w-[1071px]'>
        <ReservationStepper currentStep={2} />
      </div>

      {/* 총 대수 + 정렬 (레퍼런스: 우측 정렬, 낮은/높은 가격순) */}
      <div className='mb-4 flex w-[1071px] items-center justify-end gap-2'>
        <span className='mr-2 text-sm text-grey-060'>
          총 {sortedVehicleList?.length ?? 0}대
        </span>
        {SORT_OPTIONS.map((option) => (
          <button
            key={option.id}
            onClick={() => handleSortChange(option.id)}
            className={`rounded-full px-4 py-1.5 text-sm transition-colors ${
              sortBy === option.id
                ? 'border border-grey-020 bg-grey-000 font-bold text-primary-050 shadow-050'
                : 'font-medium text-grey-050 hover:text-primary-050'
            }`}
          >
            {option.name}
          </button>
        ))}
      </div>

      {sortedVehicleList?.length === 0 ? (
        <div className='w-[1071px]'>
          <SearchFilterNull />
        </div>
      ) : (
        <div className='flex w-[1071px] flex-col gap-4'>
          {sortedVehicleList?.map((vehicleModel) => (
            <SearchResultCard
              key={vehicleModel.id}
              vehicleModel={vehicleModel}
              unavailable={vehicleModel.availableCount === 0}
              onReserve={handleReserve}
            />
          ))}
        </div>
      )}
    </>
  );
};

export default SearchResult;
